/**
 * Tournament Configuration — the Code of Conduct catalogue (spec `06-hearings.md` §2 and §7,
 * Configuration §05).
 *
 * **What this surface is.** The one place the offence catalogue is loaded, and the one place a
 * Technical Delegate can see what the create-hearing modal, the register's charge chips and
 * three served documents are going to quote. §2 requires the catalogue to be bound to the
 * specific Code edition in force and forbids a hard-coded list, so there is no built-in
 * catalogue anywhere in this system: what is here is what somebody loaded.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PASTE, REVIEW, LOAD — AND THE REVIEW STEP IS THE POINT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Thirty-odd articles are transcribed from a PDF in one sitting, so the intake is a paste box
 * and not thirty forms. The parse is deliberately forgiving about the SHAPE of what is pasted
 * — tab-separated, pipe-separated, or the article code and level followed by the wording — and
 * completely unforgiving about the CONTENT: a line it cannot read is reported by number and
 * nothing is sent.
 *
 * Between the paste and the load there is a review table, and it is not decoration. The wording
 * loaded here is quoted verbatim into the Hearing Letter, the minutes and the decision letter
 * served on a team manager, and the moment to notice that the paste lost the last five lines
 * of Level 1 is before it becomes what the tournament charges people under — not on the
 * morning of a hearing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO BOUND EDITION MEANS NO LOAD, AND THE PANEL SAYS SO ON SCREEN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `bound_instrument` is Configuration §05's record of WHICH Code edition this tournament runs
 * under. Nothing on this panel creates one, and where there is none the intake is refused with
 * the reason rather than defaulting to an edition: article lettering shifts between editions,
 * and a catalogue loaded against a guess is a catalogue that mis-cites every case built on it.
 *
 * **Nothing is optimistic.** The panel re-renders from the server's own reply to the load, and
 * the reply carries how many rows were replaced and how many landed — because *"29 replaced by
 * 24"* is the sentence that makes a lost line visible.
 */

import { useState } from "react";

import { HEARING_LEVELS, type HearingLevel } from "@hockeytd/shared";

import { ApiError, putCodeOfConduct } from "../../api/client.js";
import type {
  CodeOfConductArticleRequest,
  CodeOfConductBody,
  CodeOfConductWriteBody,
} from "../../api/types.js";

/**
 * The write, behind one seam — `OwnershipPanel`'s idiom, for its reason: a component test
 * drives the panel without a network.
 */
export interface CatalogueWriter {
  load: (articles: readonly CodeOfConductArticleRequest[]) => Promise<CodeOfConductWriteBody>;
}

export const LIVE_CATALOGUE_WRITER: CatalogueWriter = { load: putCodeOfConduct };

/** What {@link parseCatalogueText} produces: either a set to review, or the line that broke. */
export type CatalogueParse =
  | { ok: true; articles: CodeOfConductArticleRequest[] }
  | { ok: false; message: string };

/**
 * Reads a pasted catalogue.
 *
 * One line per article. The article code comes first, the level second, and the wording is
 * everything after them — so a wording containing the separator is not truncated, which
 * matters because Code wording contains commas and dashes routinely.
 *
 * Three separators are accepted — a tab, a pipe, or two or more spaces — because a paste out
 * of a PDF, out of a spreadsheet and out of a text editor produce those three and a TD should
 * not have to know which he has. Blank lines are skipped; a line beginning `#` is a comment,
 * so a set can be annotated while it is being checked against the bound edition.
 *
 * Nothing is normalised beyond trimming: the wording is quoted verbatim into three served
 * documents, and a transcription this parser "improved" would be a quotation the Code does not
 * contain.
 */
export function parseCatalogueText(text: string): CatalogueParse {
  const articles: CodeOfConductArticleRequest[] = [];
  const lines = text.split(/\r?\n/);

  for (const [index, raw] of lines.entries()) {
    const line = raw.trim();
    if (line === "" || line.startsWith("#")) continue;

    const parts = line
      .split(/\t|\s*\|\s*|\s{2,}/)
      .map((part) => part.trim())
      .filter((part) => part !== "");

    const number = index + 1;
    if (parts.length < 3) {
      return {
        ok: false,
        message:
          `Line ${String(number)} could not be read: “${line}”. Each line is the article ` +
          `code, the level and the wording, separated by a tab, a pipe or two or more ` +
          `spaces — for example: 2.2.f | 1 | Inappropriate and/or deliberate physical contact.`,
      };
    }

    const [article, levelText, ...rest] = parts;
    const level = Number.parseInt(levelText ?? "", 10);
    if (level !== 1 && level !== 2 && level !== 3) {
      return {
        ok: false,
        message:
          `Line ${String(number)} has level “${levelText ?? ""}”. The Code of Conduct has ` +
          `three levels — 1, 2 and 3 — and each carries its own minimum penalty.`,
      };
    }

    articles.push({
      article: article ?? "",
      level: level as HearingLevel,
      // Rejoined with the separator the split removed, so a wording that legitimately
      // contained two spaces is not silently reflowed into one.
      articleText: rest.join(" ").trim(),
    });
  }

  if (articles.length === 0) {
    return {
      ok: false,
      message:
        "Nothing was read from the paste. The catalogue is loaded as a whole set, and an " +
        "empty one would clear what is already there rather than leave it alone.",
    };
  }

  return { ok: true, articles };
}

export interface CodeOfConductPanelProps {
  /** The catalogue as fetched. Held as seed state — there is one catalogue per tournament. */
  initialView: CodeOfConductBody;
  writer?: CatalogueWriter;
}

export function CodeOfConductPanel({
  initialView,
  writer = LIVE_CATALOGUE_WRITER,
}: CodeOfConductPanelProps): React.JSX.Element {
  const [view, setView] = useState<CodeOfConductBody>(initialView);
  const [text, setText] = useState("");
  const [review, setReview] = useState<CodeOfConductArticleRequest[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const bound = view.instrument !== null;

  function check(): void {
    setNote(null);
    const parsed = parseCatalogueText(text);
    if (!parsed.ok) {
      setReview(null);
      setProblem(parsed.message);
      return;
    }
    setProblem(null);
    setReview(parsed.articles);
  }

  async function load(): Promise<void> {
    if (review === null) return;
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      const result = await writer.load(review);
      // The server's reply, never the request echoed back: the catalogue on screen must be the
      // catalogue the tournament will charge people under.
      setView(result.catalogue);
      setReview(null);
      setText("");
      setNote(
        `${String(result.loaded)} articles loaded` +
          (result.replaced === 0
            ? "."
            : `, replacing the ${String(result.replaced)} that were there before.`),
      );
    } catch (error) {
      setProblem(
        error instanceof ApiError
          ? error.message
          : "The catalogue was not loaded. What was there before is unchanged.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel panel--d1" aria-labelledby="cfgCocTitle" style={{ marginTop: 22 }} data-howto="code-of-conduct">
      <div className="panel__head">
        <h3 className="panel__title" id="cfgCocTitle">
          Code Of Conduct Catalogue
        </h3>
        <span className="panel__note">Bound To The Edition In Force · Loaded, Never Built In</span>
        <span className="spacer" />
        <span
          className={`statuspill ${view.articleCount === 0 ? "statuspill--warn" : "statuspill--good"}`}
          data-testid="coc-count"
        >
          <span className="sdot" aria-hidden="true" />
          {view.articleCount === 0
            ? "No articles loaded"
            : `${String(view.articleCount)} articles loaded`}
        </span>
      </div>

      {/* ── Which edition, or the honest refusal ─────────────────────────── */}
      {bound ? (
        <div className="panel__foot" data-testid="coc-instrument">
          <b>
            {view.instrument?.title}, {view.instrument?.versionLabel}
          </b>{" "}
          — effective {view.instrument?.effectiveDate}. Generated documents cite it as{" "}
          <i>{view.instrument?.citationString}</i>. Every article below belongs to this edition
          and to no other, because the lettering shifts between editions and a case cited from
          the wrong one is a case cited wrongly.
        </div>
      ) : (
        <div className="emptystate" data-testid="coc-unbound">
          <b>No Code Of Conduct Edition Is Bound To This Tournament</b>
          There is nothing for a catalogue to be the catalogue of, so nothing can be loaded
          here. Bind the edition in force — its title, its version, its effective date and the
          citation string generated documents carry — under Configuration Section 05 first. The
          catalogue is not loaded against a default edition: article lettering shifts between
          editions, so a guess here would mis-cite every case built on it.
        </div>
      )}

      {/* ── The catalogue as it stands ───────────────────────────────────── */}
      {bound ? (
        <div className="kv" data-testid="coc-levels">
          {HEARING_LEVELS.map((level: HearingLevel) => {
            const group = view.levels.find((one) => one.level === level);
            return (
              <div className="field" key={level}>
                <span className="field__label">{group?.label ?? `Level ${String(level)}`}</span>
                <span className="field__sub">
                  <b>Minimum penalty:</b> {group?.minimumPenalty}
                  {group?.minimumPenaltyIsConfigured === false ? (
                    <>
                      {" "}
                      <i>
                        (not configured for this tournament — this is the standard rung, shown
                        so the field is not blank)
                      </i>
                    </>
                  ) : null}
                </span>
                {group === undefined || group.articles.length === 0 ? (
                  <span className="field__sub">No articles loaded at this level.</span>
                ) : (
                  <ul>
                    {group.articles.map((row) => (
                      <li key={row.id}>
                        <b>{row.article}</b> — {row.articleText}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* ── Intake ───────────────────────────────────────────────────────── */}
      {bound ? (
        <div className="field">
          <span className="field__label">Load The Catalogue</span>
          <span className="field__sub">
            One line per article: the code, the level, then the wording, separated by a tab, a
            pipe or two or more spaces. Lines beginning <code>#</code> are ignored, so a set can
            be annotated while it is checked against the bound edition. Loading REPLACES the
            whole catalogue — a correction is a reload of the corrected set — and it changes no
            case: every charge already laid keeps the wording it was laid under.
          </span>
          <textarea
            aria-label="Catalogue paste"
            rows={8}
            value={text}
            placeholder={"2.2.f | 1 | Inappropriate and/or deliberate physical contact\n2.3.d | 2 | Intimidation of an umpire or third person"}
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
          <div className="hmodal__acts">
            <button
              type="button"
              className="gbtn"
              disabled={busy || text.trim() === ""}
              onClick={check}
            >
              Check The Paste
            </button>
            <button
              type="button"
              className="gbtn gbtn--solid"
              disabled={busy || review === null}
              onClick={() => {
                void load();
              }}
            >
              {busy ? "Loading…" : "Load Catalogue"}
            </button>
          </div>
        </div>
      ) : null}

      {problem === null ? null : (
        <p className="rowerror" role="alert" data-testid="coc-error">
          {problem}
        </p>
      )}

      {note === null ? null : (
        <p className="panel__foot" role="status" data-testid="coc-note">
          {note}
        </p>
      )}

      {review === null ? null : (
        <div data-testid="coc-review">
          <p className="field__sub">
            <b>
              {String(review.length)} articles read — review them before loading.
            </b>{" "}
            This wording is quoted verbatim into the Hearing Letter, the minutes and the
            decision letter served on a team manager, so the moment to notice a line the paste
            lost is now.
          </p>
          <ul>
            {review.map((row, index) => (
              <li key={`${row.article}-${String(index)}`}>
                <b>{row.article}</b> · Level {row.level} — {row.articleText}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="panel__foot">
        <b>The catalogue is data, and is never built into this system.</b> The offence list is
        bound to the specific Code edition in force for this tournament, because article
        lettering shifts between editions and past case letters cite earlier letterings — so
        nothing here ships with a catalogue, and an unbound tournament is told to bind an
        edition rather than being given one. Reloading changes no case: a charge keeps the
        wording it was laid under, so a letter already issued goes on quoting what it quoted.
      </div>
    </section>
  );
}
